import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import ic_add_b from "../../assets/images/icons/add.svg";
import arrow from "../../assets/images/icons/arrow-down.svg";
import empty_prescription from "../../assets/images/icons/empty-prescription.svg";
import ic_add from "../../assets/images/icons/add.svg";
import {
  GeneralButton,
  GeneralRadio,
  InfiniteScroll,
  Modal,
  SearchComponent,
} from "../../components";
import { EndPoints } from "../../Constants/endpoints";
import { useAppMediaQuery, useFilter, useGetPersonalInfo } from "../../hooks";
import i18n from "../../i18n";
import {
  PatientDetails,
  PrescriptionType,
} from "../../interface/GeneralResponse";
import {
  clearPatientDetails,
  setPatientDetails,
} from "../../redux/reducers/patientSlice";
import {
  clearPrescriptions,
  setPrescriptions,
} from "../../redux/reducers/prescriptionsSlice";
import {
  clearSelectedPrescription,
  setSelectedPrescription,
} from "../../redux/reducers/selectedPrescriptionSlice";
import { RootState } from "../../redux/store";
import { IsProfileCompleted, MedicationLoader } from "../../Share";
import PatientDetailsForm from "./PatientDetailsForm";
import PrescriptionDetailsModal from "./PrescriptionDetailsModal";

const searchSchema = z.object({
  search: z.string().optional(),
});

type SearchFormData = z.infer<typeof searchSchema>;

const MyPrescriptions = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { t } = useTranslation();
  const queryClient = useQueryClient(); 
  const selectedTemplate = useSelector(
    (state: RootState) => state.selectedPrescription.selectedTemplate,
  );

  const [selectedPrescriptionId, setSelectedPrescriptionId] = useState<
    number | null
  >(null);

  const handleOpenDetails = (id: number) => setSelectedPrescriptionId(id);
  const handleCloseDetails = () => setSelectedPrescriptionId(null);
  const handleEdit = (id: number) => navigate(`/my_prescriptions/edit/${id}`);

  const [prescriptionIdForLink, setPrescriptionIdForLink] = useState<
    number | string
  >();
  const [isModalOpenWithTemplate, setIsModalOpenWithTemplate] = useState(false);
  const [isModalOpenNoTemplate, setIsModalOpenNoTemplate] = useState(false);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [selectedOption, setSelectedOption] = useState<string>("");

  const [prescriptionsData, setPrescriptionsData] =
    useState<PrescriptionType[]>();
  const [templatesData, setTemplatesData] = useState<PrescriptionType[]>();

  const searchMethods = useForm<SearchFormData>({
    resolver: zodResolver(searchSchema),
    defaultValues: {
      search: "",
    },
  }); 
  const { watch } = searchMethods;
  const searchQuery = watch("search")?.trim().toLowerCase() || "";

  const [templateSearchQuery, setTemplateSearchQuery] = useState<string>("");

  useEffect(() => {
    if (prescriptionsData) {
      dispatch(setPrescriptions(prescriptionsData));
    } else {
      dispatch(clearPrescriptions());
    }
  }, [prescriptionsData, dispatch]);

  useEffect(() => {
    dispatch(clearPatientDetails());
    dispatch(clearSelectedPrescription());
  }, [dispatch]);

  const onSubmit = (data: SearchFormData) => {};

  const onTemplateSearchSubmit = (search: string) => {
    setTemplateSearchQuery(search?.trim().toLowerCase() || "");
  };

  const handleOnLinkToPatient = (Id: number) => {
    const selectedTemplate = (prescriptionsData ?? templatesData)?.find(
      (item: any) => item?.Id == Id,
    );
    setSelectedPrescriptionId(null);
    setCurrentStep(3);
    setSelectedOption("from_templates");
    dispatch(setSelectedPrescription(selectedTemplate ?? null));
    setIsModalOpenWithTemplate(true);
    setPrescriptionIdForLink(Id);
  };

  // Handle Add New Prescription button click
  const handleAddNewPrescription = () => {
    setIsModalOpenWithTemplate(true);
    setCurrentStep(1);
    setSelectedOption("");
    dispatch(setSelectedPrescription(null));
  };

  // Handle modal close
  const handleCloseModalWithTemplate = () => {
    setIsModalOpenWithTemplate(false);
    setCurrentStep(1);
    setSelectedOption("");
    dispatch(setSelectedPrescription(null));
  };
  const handleCloseModalNoTemplate = () => {
    setIsModalOpenNoTemplate(false);
  };

  const handleOptionSelect = (option: string) => {
    setSelectedOption(option);
    if (option === "new_prescription") {
      setIsModalOpenNoTemplate(true);
      setIsModalOpenWithTemplate(false);
    } else {
      setCurrentStep(2);
    }
  };

  const handleTemplateSelect = (template: PrescriptionType | null) => {
    if (selectedTemplate?.Id === template?.Id) {
      dispatch(setSelectedPrescription(null));
    } else if (template) {
      dispatch(setSelectedPrescription(template));
    }
  };

  const handleAddPrescriptionWithTemplate = (patientData: PatientDetails) => {
    dispatch(setPatientDetails(patientData));
    navigate(`/add_prescription_by_template/${patientData?.Id}`);
  };
  const handleAddPrescriptionNoTemplate = (patientData: PatientDetails) => {
    dispatch(setPatientDetails(patientData));
    navigate(`/add_prescription_to_patient/${patientData?.Id}`);
  };

  const prescriptions = useSelector(
    (state: RootState) => state.prescriptions.prescriptions,
  );

  const handleTemplateRadioClick = (template: PrescriptionType) => {
    if (selectedTemplate?.Id === template.Id) {
      handleTemplateSelect(null);
    } else {
      handleTemplateSelect(template);
    }
  };
  const { isMobileOrTablet } = useAppMediaQuery();
  const { isAllowed } = useGetPersonalInfo();

  return (
    <div className="box flex min-h-[80dvh] flex-col gap-3">
      <div className="flex w-full flex-col gap-4 md:flex-wrap md:justify-between lg:flex-row">
        <FormProvider {...searchMethods}>
          <h2 className="text-nowrap text-xl font-bold max-sm:hidden max-sm:text-sm md:order-1 md:hidden">
            {t("my_prescriptions")}
          </h2>
          <div className="w-full lg:max-w-64 lg:flex-1">
            <SearchComponent
              searchOn="typing"
              value={searchQuery}
              placeholder={t("search_about_prescriptions")}
              onSearch={(value) => {
                searchMethods.setValue("search", value);
                onSubmit({ search: value });
              }}
              showSearchHistory={true}
            />
          </div>
        </FormProvider>

        <div className="flex w-full gap-3 lg:w-auto lg:flex-wrap">
          {prescriptions.length !== 0 && (
            <GeneralButton
              type="button"
              title={t(
                isMobileOrTablet ? "templates" : "create_prescription_template",
              )}
              color="lite"
              className="flex-1 justify-center max-xl:gap-1 max-xl:px-3 lg:flex-none xl:!px-6"
              icon={ic_add_b}
              onClick={() => navigate("/create_prescription_template")}
              disabled={!isAllowed}
            />
          )}
          <IsProfileCompleted
            title="complete_your_profile_to_adding_new_prescription"
            onAllowedClick={handleAddNewPrescription}
          >
            <GeneralButton
              type="button"
              title={t(
                isMobileOrTablet ? "prescription" : "add_new_prescription",
              )}
              color="blue"
              className="flex-1 justify-center max-xl:gap-1 max-xl:px-3 lg:flex-none xl:!px-6"
              icon={ic_add}
            />
          </IsProfileCompleted>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 overflow-auto">
        {prescriptionsData?.map((prescription: PrescriptionType) => {
          const formattedTimestamp = String(prescription.CreatedAt).split(
            "٫",
          )?.[0];
          return (
            <div
              key={prescription.Id}
              className="main_warper cursor-pointer justify-between"
              onClick={() => handleOpenDetails(prescription.Id)}
            >
              <div className="flex flex-col gap-1">
                <h3 className="line-clamp-1 font-semibold">
                  {prescription.Case}
                </h3>
                <p className="text-sm text-body-text">
                  {t("created_on")}{" "}
                  {moment
                    .unix(Number(formattedTimestamp))
                    .locale(i18n?.language?.slice(0, 2))
                    .format("DD MMMM YYYY")}
                </p>
              </div>
              <img
                src={arrow}
                className="ltr:-rotate-90 rtl:rotate-90"
                alt={t("details")}
              />
            </div>
          );
        })}
        <InfiniteScroll
          Count={8}
          skeletonCount={2}
          extraDataKey="Prescriptions"
          endpoint={EndPoints.GetPrescriptions}
          getAllParams={{
            Name: searchQuery,
          }}
          getData={(data) => setPrescriptionsData(data)}
          renderSkeleton={
            <div className="ps-8">
              <MedicationLoader />
            </div>
          }
          noDataElement={
            <div className="flex h-full flex-col items-center justify-center py-10 text-center">
              <img
                src={empty_prescription}
                alt="Empty"
                className="mx-auto mb-4 max-w-48"
              />
              {!searchQuery ? (
                <>
                  <p className="mb-4 font-bold">
                    {t("create_your_first_prescription_template_easily")}
                  </p>
                  <IsProfileCompleted
                    title="complete_your_profile_to_creating_prescription_template"
                    onAllowedClick={() => {
                      navigate("/create_prescription_template");
                    }}
                  >
                    <GeneralButton
                      type="button"
                      title={t("create_prescription_template")}
                      color="blue"
                    />
                  </IsProfileCompleted>
                </>
              ) : (
                <p className="mb-4 font-bold">{t("no_results")}</p>
              )}
            </div>
          }
        />
      </div>
      {/* )} */}

      {/* Modals */}
      <Modal
        isOpen={isModalOpenWithTemplate}
        onClose={handleCloseModalWithTemplate}
        header={t("add_prescription")}
        ariaLabel="Add New Prescription Modal"
      >
        {currentStep === 1 && (
          <div className="flex flex-col space-y-2">
            <div
              className="flex cursor-pointer items-center rounded-lg p-4 text-secondaryDark hover:bg-primary-bg hover:font-bold hover:text-primary"
              onClick={() => handleOptionSelect("new_prescription")}
            >
              {t("new_prescription")}
            </div>

            {prescriptions.length !== 0 && (
              <div
                className="flex cursor-pointer items-center rounded-lg p-4 text-secondaryDark hover:bg-primary-bg hover:font-bold hover:text-primary"
                onClick={() => handleOptionSelect("from_templates")}
              >
                {t("from_templates")}
              </div>
            )}
          </div>
        )}

        {currentStep === 2 && selectedOption === "from_templates" && (
          <div className="flex flex-col gap-4">
            <h3 className="text-xl font-semibold">{t("select_a_template")}</h3>

            <SearchComponent
              searchOn="typing"
              value={templateSearchQuery}
              placeholder={t("search_templates")}
              onSearch={onTemplateSearchSubmit}
            />

            {/* Template List */}

            <div className="flex max-h-60 min-h-60 flex-col gap-5 overflow-y-auto">
              {/* in the return block of AddPrescriptionModal */}

              {templatesData?.map((template) => (
                <GeneralRadio
                  key={template.Id}
                  name="template"
                  label={
                    <div className="flex flex-col gap-1">
                      <span>{template?.Case}</span>
                      <p className="text-sm text-body-text">
                        {t("created")}{" "}
                        {moment
                          .unix(parseInt(template?.CreatedAt))
                          .format("DD-MMM-YYYY")}
                      </p>
                    </div>
                  }
                  value={template.Id.toString()}
                  checked={selectedTemplate?.Id === template.Id}
                  onClick={() => handleTemplateRadioClick(template)}
                />
              ))}
              <InfiniteScroll
                Count={4}
                skeletonCount={2}
                extraDataKey="Prescriptions"
                endpoint={EndPoints.GetPrescriptions}
                getAllParams={{
                  Name: templateSearchQuery,
                  template: true,
                }}
                getData={(data) => setTemplatesData(data)}
                renderSkeleton={
                  <div className="ps-8">
                    <MedicationLoader />
                  </div>
                }
              />
            </div>

            <div className="flex justify-end gap-3">
              <GeneralButton
                type="button"
                title={t("previous")}
                color="lite"
                onClick={() => setCurrentStep(1)}
              />
              <GeneralButton
                type="button"
                title={t("next")}
                color="blue"
                disabled={!selectedTemplate}
                onClick={() => setCurrentStep(3)}
              />
            </div>
          </div>
        )}

        {currentStep === 3 && selectedOption === "from_templates" && (
          <div className="flex flex-col gap-4">
            <PatientDetailsForm
              prescriptionId={prescriptionIdForLink}
              onPrevious={() => setCurrentStep(2)}
              onSubmit={handleAddPrescriptionWithTemplate}
            />
          </div>
        )}
      </Modal>

      <Modal
        isOpen={isModalOpenNoTemplate}
        onClose={handleCloseModalNoTemplate}
        header={t("add_prescription")}
        ariaLabel="Add New Prescription Modal"
      >
        <PatientDetailsForm
          withButtons
          prescriptionId={prescriptionIdForLink}
          onPrevious={() => {
            setIsModalOpenNoTemplate(false);
            setIsModalOpenWithTemplate(true);
          }}
          onSubmit={handleAddPrescriptionNoTemplate}
        />
      </Modal>

      {selectedPrescriptionId && (
        <PrescriptionDetailsModal
          isOpen={!!selectedPrescriptionId}
          onClose={handleCloseDetails}
          prescriptionId={selectedPrescriptionId}
          onEdit={handleEdit}
          OnLinkPatient={handleOnLinkToPatient}
          onDeleted={() => {
            queryClient.refetchQueries({
              queryKey: [EndPoints.GetPrescriptions],
              exact: false,
            });
          }}
        />
      )}
    </div>
  );
};

export default MyPrescriptions;
